import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import axios from "axios";
import moment from "moment";
import { Select, message, Space, Checkbox } from "antd";
import {
  ModalItem,
  InputItem,
  TextAreaItem,
  SelectItem,
  DatePickerItem,
  SelectEmpleadosItem,
} from "../../../../Components/Items";

const { Option } = Select;

// Las mismas tres que reconoce el backend. La prioridad es un número para poder ordenar por ella.
export const PRIORIDADES = [
  { valor: 0, etiqueta: "Baja", color: "#6b7280" },
  { valor: 1, etiqueta: "Normal", color: "#0e7490" },
  { valor: 2, etiqueta: "Alta", color: "#b45309" },
  { valor: 3, etiqueta: "Urgente", color: "#b91c1c" },
];

export const ESTATUS_TAREA = {
  pendiente: { etiqueta: "Pendiente", color: "#b45309" },
  terminada: { etiqueta: "Terminada", color: "#15803d" },
  cancelada: { etiqueta: "Cancelada", color: "#6b7280" },
};

//Alta y edición de una tarea de habitación. Es el MISMO modal en las dos entradas: la agenda del hotel
//y el detalle de una habitación en el mapa. La diferencia es `habitacionFija`: cuando se abre desde un
//cuarto no se puede cambiar la habitación, porque ya se sabe de cuál se está hablando.
const ModalTareaHabitacion = ({
  visible,
  onCancel,
  onGuardado,
  tarea,
  habitacionFija,
  habitaciones = [],
  fechaSugerida,
  empresaId,
  headersToken,
}) => {
  const [valores, setValores] = useState({});
  const [guardando, setGuardando] = useState(false);

  const esEdicion = !!(tarea && tarea._id);

  useEffect(() => {
    if (!visible) return;
    // Se reconstruye al abrir y no en cada render: si no, editar un campo lo revertiría al valor
    // original en el siguiente ciclo.
    setValores({
      titulo: tarea?.titulo || "",
      descripcion: tarea?.descripcion || "",
      habitacionId: tarea?.habitacionId
        ? String(tarea.habitacionId)
        : habitacionFija
        ? String(habitacionFija)
        : undefined,
      responsableEmpleadoId: tarea?.responsableEmpleadoId
        ? String(tarea.responsableEmpleadoId)
        : undefined,
      fechaInicio: tarea?.fechaInicio
        ? moment(tarea.fechaInicio)
        : fechaSugerida
        ? moment(fechaSugerida)
        : moment(),
      todoElDia: tarea ? tarea.todoElDia !== false : true,
      prioridad: tarea && tarea.prioridad !== undefined ? tarea.prioridad : 1,
    });
  }, [visible, tarea, habitacionFija, fechaSugerida]);

  const actualizar = (cambios) => setValores((previo) => ({ ...previo, ...cambios }));

  const guardar = () => {
    if (!valores.titulo || !valores.titulo.trim()) {
      return message.warning("La tarea necesita un título");
    }
    if (!valores.habitacionId) return message.warning("Elige la habitación");
    if (!valores.fechaInicio) return message.warning("Elige la fecha");

    setGuardando(true);
    axios
      .post(
        esEdicion ? "hoteleria/editarTareaHabitacion" : "hoteleria/agregarTareaHabitacion",
        {
          empresaId,
          tareaId: tarea?._id,
          habitacionId: valores.habitacionId,
          titulo: valores.titulo.trim(),
          descripcion: valores.descripcion,
          responsableEmpleadoId: valores.responsableEmpleadoId || null,
          // Una tarea de todo el día se manda como "YYYY-MM-DD" para que el backend la ancle al día
          // correcto; una con hora va como instante. Misma regla que el resto del módulo.
          fechaInicio: valores.todoElDia
            ? valores.fechaInicio.format("YYYY-MM-DD")
            : valores.fechaInicio.toISOString(),
          todoElDia: valores.todoElDia,
          prioridad: valores.prioridad,
        },
        { headers: headersToken }
      )
      .then((res) => {
        if (res.data.success === 2) {
          message.success(esEdicion ? "Tarea actualizada" : "Tarea creada");
          onGuardado && onGuardado();
          return;
        }
        message.warning(res.data.message || res.data.error || "No se pudo guardar la tarea");
      })
      .catch((error) => {
        console.error("Error al guardar la tarea:", error);
        message.error("No se pudo guardar la tarea. Revisa tu conexión.");
      })
      .finally(() => setGuardando(false));
  };

  return (
    <ModalItem
      title={esEdicion ? "Editar tarea" : "Nueva tarea"}
      visible={visible}
      onCancel={onCancel}
      onOk={guardar}
      okText={esEdicion ? "Guardar cambios" : "Crear tarea"}
      confirmLoading={guardando}
      spinning={guardando}
      footer
    >
      <div style={{ marginBottom: 12 }}>
        <div style={{ marginBottom: 4, color: "#6b7280" }}>Título</div>
        <InputItem
          value={valores.titulo}
          placeholder="Limpieza profunda, revisar minisplit..."
          onChange={(e) => actualizar({ titulo: e.target.value })}
        />
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ marginBottom: 4, color: "#6b7280" }}>Habitación</div>
          <SelectItem
            width="100%"
            placeholder="Habitación"
            value={valores.habitacionId}
            // Abierto desde el cuarto: la habitación es la que se estaba viendo y no se cambia.
            disabled={!!habitacionFija}
            onChange={(valor) => actualizar({ habitacionId: valor })}
          >
            {habitaciones.map((valorHabitacion) => {
              // Una sola cadena y no varios hijos: el filterOption de SelectItem hace
              // children.toLowerCase() y truena si children es un arreglo.
              const etiqueta =
                `${valorHabitacion.numero}` +
                (valorHabitacion.nombre ? ` - ${valorHabitacion.nombre}` : "") +
                (valorHabitacion.nivelNombre ? ` (${valorHabitacion.nivelNombre})` : "");
              return (
                <Option key={valorHabitacion._id} value={String(valorHabitacion._id)}>
                  {etiqueta}
                </Option>
              );
            })}
          </SelectItem>
        </div>
        <div style={{ width: 140 }}>
          <div style={{ marginBottom: 4, color: "#6b7280" }}>Prioridad</div>
          <SelectItem
            width="100%"
            value={valores.prioridad}
            onChange={(valor) => actualizar({ prioridad: valor })}
          >
            {PRIORIDADES.map((valorPrioridad) => (
              <Option key={valorPrioridad.valor} value={valorPrioridad.valor}>
                {valorPrioridad.etiqueta}
              </Option>
            ))}
          </SelectItem>
        </div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <div style={{ marginBottom: 4, color: "#6b7280" }}>Responsable</div>
        <SelectEmpleadosItem
          empresaId={empresaId}
          headersToken={headersToken}
          value={valores.responsableEmpleadoId}
          onChange={(valor) => actualizar({ responsableEmpleadoId: valor })}
          width="100%"
        />
        <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>
          Se puede dejar vacío y asignarlo después.
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 4, color: "#6b7280" }}>Cuándo</div>
        <Space>
          <DatePickerItem
            width={170}
            value={valores.fechaInicio}
            allowClear={false}
            showTime={!valores.todoElDia && { format: "HH:mm" }}
            format={valores.todoElDia ? "DD/MM/YYYY" : "DD/MM/YYYY HH:mm"}
            onChange={(valor) => actualizar({ fechaInicio: valor || moment() })}
          />
          <Checkbox
            checked={valores.todoElDia}
            onChange={(e) => actualizar({ todoElDia: e.target.checked })}
          >
            Todo el día
          </Checkbox>
        </Space>
      </div>

      <div style={{ marginBottom: 8 }}>
        <div style={{ marginBottom: 4, color: "#6b7280" }}>Detalle (opcional)</div>
        <TextAreaItem
          rows={3}
          value={valores.descripcion}
          onChange={(e) => actualizar({ descripcion: e.target.value })}
        />
      </div>
    </ModalItem>
  );
};

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
});

export default connect(mapStateToProps)(ModalTareaHabitacion);
